// ------------------------------------------------------------
// assignmentUtils.js
// ------------------------------------------------------------
// Shared utilities for:
// - Loading driver/car assignment history
// - Getting week date ranges
// - Matching which car a driver was driving in a specific week
// ------------------------------------------------------------

import { db } from "../../firebase/firebase";
import { collection, getDocs } from "firebase/firestore";

// ------------------------------------------------------------
// 1️⃣ WEEK CONSTANTS (same as your system)
// ------------------------------------------------------------
export const WEEK_KEYS = ["week1", "week2", "week3", "week4", "week5"];

export const weekKeyToIndex = (weekKey) => {
  const idx = WEEK_KEYS.indexOf(weekKey);
  return idx === -1 ? null : idx + 1; // week1 -> 1, week2 -> 2...
};

// ------------------------------------------------------------
// 2️⃣ Get full date range for a specific week of a month
//    monthStr example: "2025-11"
// ------------------------------------------------------------
export function getWeekDateRange(monthStr, weekIndex) {
  if (!monthStr || !weekIndex) return null;

  const [year, month] = monthStr.split("-").map(Number);
  const startDay = [null, 1, 8, 15, 22, 29][weekIndex]; // 1–7, 8–14, ...

  if (!startDay) return null;

  const start = new Date(year, month - 1, startDay);
  const end = new Date(year, month - 1, startDay + 6); // 7-day range

  // Clamp end to last day of month
  const lastDay = new Date(year, month, 0).getDate();
  if (end.getDate() > lastDay) end.setDate(lastDay);

  // Normalize times
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

// ------------------------------------------------------------
// 3️⃣ Load all assignment history
//    Firestore Collection: driverCarAssignments
//    Your structure already includes:
//    - driverId
//    - driverName
//    - vehicleId, vehicleNumber, vehicleType
//    - startDate (timestamp)
//    - endDate (null or timestamp)
// ------------------------------------------------------------
export async function loadAssignmentsMap() {
  console.log("🔄 LOADING ASSIGNMENTS: Fetching from driverCarAssignments collection");
  const snap = await getDocs(collection(db, "driverCarAssignments"));
  
  console.log(`📋 ASSIGNMENTS: Loaded ${snap.size} assignment documents`);
  
  const map = {}; // driverId → [assignments]

  snap.forEach((docSnap) => {
    const d = docSnap.data();
    if (!d.driverId) {
      console.warn(`⚠️ ASSIGNMENT SKIP: Document ${docSnap.id} has no driverId`);
      return;
    }

    if (!map[d.driverId]) {
      map[d.driverId] = [];
    }

    // Convert dates properly
    const startDate = d.startDate?.toDate
      ? d.startDate.toDate()
      : new Date(d.startDate);
      
    const endDate = d.endDate
      ? (d.endDate.toDate ? d.endDate.toDate() : new Date(d.endDate))
      : null;

    console.log(`📝 ASSIGNMENT: Driver ${d.driverId} (${d.driverName}) → Car ${d.vehicleNumber} from ${startDate.toISOString().split('T')[0]} to ${endDate ? endDate.toISOString().split('T')[0] : 'ongoing'}`);

    map[d.driverId].push({
      id: docSnap.id,
      driverId: d.driverId,
      driverName: d.driverName,
      company: d.company || "",
      vehicleId: d.vehicleId,
      vehicleNumber: d.vehicleNumber,
      vehicleType: d.vehicleType,
      startDate: startDate,
      endDate: endDate,

      // Driver cost automation fields. Existing old assignment docs may not
      // have these fields, so every consumer must treat missing values as
      // not configured and keep the old copied/fetched expense behavior.
      driverRole: d.driverRole || d.assignmentRole || d.role || "",
      costConfigured: d.costConfigured === true,
      monthlyInstallment: Number(d.monthlyInstallment || 0),
      secondDriverMonthlyExpense: Number(d.secondDriverMonthlyExpense || 500),
      installmentEffectiveFrom: d.installmentEffectiveFrom || null,
      installmentMode: d.installmentMode || "",

      // Installment number tracking fields, e.g. 2/30 or 34/36.
      // These must be carried into the car-sheet builder so MONTHLY FIXED COST
      // can display the correct installment number in preview and exports.
      installmentPlanEnabled: d.installmentPlanEnabled === true,
      installmentCurrentNo: Number(d.installmentCurrentNo || 0),
      installmentTotalCount: Number(d.installmentTotalCount || 0),
      installmentBaseMonth: d.installmentBaseMonth || "",

      // Month-versioned assignment cost history. Old assignment docs do not
      // have this field; autoDriverCosts keeps the legacy top-level behavior
      // until the first month-based revision is saved.
      costPlanHistory: Array.isArray(d.costPlanHistory) ? d.costPlanHistory : [],
      costPlanHistoryEnabled: d.costPlanHistoryEnabled === true,
    });
  });

  // Sort each driver's assignments by startDate ASC
  Object.values(map).forEach((list) => {
    list.sort((a, b) => a.startDate - b.startDate);
  });

  console.log(`✅ ASSIGNMENTS MAP: Created with ${Object.keys(map).length} unique drivers`);
  
  // Log first few assignments for verification
  const sampleDrivers = Object.keys(map).slice(0, 3);
  sampleDrivers.forEach(driverId => {
    console.log(`👤 Driver ${driverId} has ${map[driverId].length} assignments:`);
    map[driverId].forEach((a, i) => {
      console.log(`   ${i+1}. ${a.vehicleNumber} (${a.company}) ${a.startDate.toISOString().split('T')[0]} - ${a.endDate ? a.endDate.toISOString().split('T')[0] : 'ongoing'}`);
    });
  });

  return map;
}

// ------------------------------------------------------------
// 4️⃣ Find which car a driver was using for a given week
//    Inputs:
//    - driverId
//    - month ("2025-11")
//    - weekIndex (1..5)
//    - assignmentsMap from loadAssignmentsMap()
// ------------------------------------------------------------
export function getCarForDriverWeek({ driverId, month, weekIndex, assignmentsMap }) {
  const assignments = assignmentsMap[driverId];
  if (!assignments || assignments.length === 0) {
    console.warn(`❌ getCarForDriverWeek: No assignments found for driver ${driverId}`);
    return null;
  }

  // Get precise week range
  const range = getWeekDateRange(month, weekIndex);
  if (!range) {
    console.warn(`❌ getCarForDriverWeek: Invalid week range for month ${month}, week ${weekIndex}`);
    return null;
  }

  console.log(`🔍 getCarForDriverWeek: Looking for driver ${driverId} in week ${weekIndex} (${range.start.toISOString().split('T')[0]} to ${range.end.toISOString().split('T')[0]})`);

  // Use the week *start* date as the reference
  const ref = range.start;
  ref.setHours(0, 0, 0, 0);

  console.log(`📅 Reference date: ${ref.toISOString()}`);
  console.log(`📊 Driver has ${assignments.length} assignments total`);

  // Find assignment where ref date is inside the assignment period
  const assignment = assignments.find(a => {
    const start = new Date(a.startDate);
    const end = a.endDate ? new Date(a.endDate) : new Date(9999, 0, 1);

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const isWithinRange = ref >= start && ref <= end;
    console.log(`   Checking assignment: ${a.vehicleNumber} from ${start.toISOString().split('T')[0]} to ${end.toISOString().split('T')[0]} - Within range? ${isWithinRange}`);

    return isWithinRange;
  });

  if (assignment) {
    console.log(`✅ FOUND: Driver ${driverId} assigned to ${assignment.vehicleNumber} in week ${weekIndex}`);
  } else {
    console.warn(`❌ NOT FOUND: No assignment for driver ${driverId} in week ${weekIndex}`);
    console.log(`   Available assignments for this driver:`);
    assignments.forEach((a, i) => {
      const start = new Date(a.startDate);
      const end = a.endDate ? new Date(a.endDate) : new Date(9999, 0, 1);
      console.log(`   ${i+1}. ${a.vehicleNumber}: ${start.toISOString().split('T')[0]} - ${end.toISOString().split('T')[0]}`);
    });
  }

  return assignment || null;
}
// ------------------------------------------------------------
// 4.5️⃣ Get ALL car splits for a driver within a week (handles mid-week car changes)
//
// Returns: [{ vehicleNumber, vehicleType, company, driverName, days, ratio, startDate, endDate }]
// ratio is normalized so that sum(ratio) = 1 across covered days.
// ------------------------------------------------------------
export function getCarSplitsForDriverWeek({ driverId, month, weekIndex, assignmentsMap }) {
  const assignments = assignmentsMap[driverId] || [];
  if (!assignments.length) return [];

  const range = getWeekDateRange(month, weekIndex);
  if (!range) return [];

  const dayMs = 24 * 60 * 60 * 1000;
  const startDay = new Date(range.start);
  startDay.setHours(0, 0, 0, 0);
  const endDay = new Date(range.end);
  endDay.setHours(0, 0, 0, 0);

  const byCar = {}; // vehicleNumber -> { days, startDate, endDate, ... }
  let coveredDays = 0;

  for (let t = startDay.getTime(); t <= endDay.getTime(); t += dayMs) {
    const cur = new Date(t);
    cur.setHours(0, 0, 0, 0);

    const a = assignments.find((x) => {
      const s = new Date(x.startDate);
      const e = x.endDate ? new Date(x.endDate) : new Date(9999, 0, 1);
      s.setHours(0, 0, 0, 0);
      e.setHours(23, 59, 59, 999);
      return cur >= s && cur <= e;
    });

    if (!a || !a.vehicleNumber) continue;

    coveredDays++;
    const key = String(a.vehicleNumber);

    if (!byCar[key]) {
      byCar[key] = {
        vehicleNumber: a.vehicleNumber,
        vehicleType: a.vehicleType || "",
        company: a.company || "",
        driverName: a.driverName || "",
        days: 0,
        startDate: cur,
        endDate: cur,
      };
    }

    byCar[key].days += 1;
    if (cur < byCar[key].startDate) byCar[key].startDate = cur;
    if (cur > byCar[key].endDate) byCar[key].endDate = cur;
  }

  if (!coveredDays) return [];

  return Object.values(byCar)
    .filter((x) => x.days > 0)
    .map((x) => ({
      ...x,
      ratio: x.days / coveredDays, // normalize across assigned days
    }))
    .sort((a, b) => a.startDate - b.startDate);
}


// ------------------------------------------------------------
// 4.6️⃣ Get ALL car splits for a driver within an exact date range
//    Used for ride-hailing reports when weekly periods are configured
//    outside standard calendar weeks, e.g. week3 = 10-Aug to 17-Aug.
//
// Returns: [{ vehicleNumber, vehicleType, company, driverName, days, ratio, startDate, endDate }]
// ratio is normalized across assigned/covered days only.
// ------------------------------------------------------------
export function getCarSplitsForDriverDateRange({ driverId, rangeStart, rangeEnd, assignmentsMap }) {
  const assignments = assignmentsMap[driverId] || [];
  if (!assignments.length) return [];

  const dayMs = 24 * 60 * 60 * 1000;
  const startDay = new Date(rangeStart);
  const endDay = new Date(rangeEnd);

  if (Number.isNaN(startDay.getTime()) || Number.isNaN(endDay.getTime())) return [];

  startDay.setHours(0, 0, 0, 0);
  endDay.setHours(0, 0, 0, 0);

  if (endDay < startDay) return [];

  const byCar = {}; // vehicleNumber -> { days, startDate, endDate, ... }
  let coveredDays = 0;

  for (let t = startDay.getTime(); t <= endDay.getTime(); t += dayMs) {
    const cur = new Date(t);
    cur.setHours(0, 0, 0, 0);

    const a = assignments.find((x) => {
      const s = new Date(x.startDate);
      const e = x.endDate ? new Date(x.endDate) : new Date(9999, 0, 1);
      s.setHours(0, 0, 0, 0);
      e.setHours(23, 59, 59, 999);
      return cur >= s && cur <= e;
    });

    if (!a || !a.vehicleNumber) continue;

    coveredDays++;
    const key = String(a.vehicleNumber);

    if (!byCar[key]) {
      byCar[key] = {
        vehicleNumber: a.vehicleNumber,
        vehicleType: a.vehicleType || "",
        company: a.company || "",
        driverName: a.driverName || "",
        days: 0,
        startDate: cur,
        endDate: cur,
      };
    }

    byCar[key].days += 1;
    if (cur < byCar[key].startDate) byCar[key].startDate = cur;
    if (cur > byCar[key].endDate) byCar[key].endDate = cur;
  }

  if (!coveredDays) return [];

  return Object.values(byCar)
    .filter((x) => x.days > 0)
    .map((x) => ({
      ...x,
      ratio: x.days / coveredDays,
    }))
    .sort((a, b) => a.startDate - b.startDate);
}

// ------------------------------------------------------------
// 5️⃣ Find car assignment using EXACT DATE (Manual Trips)
//    Inputs:
//    - driverId
//    - date (YYYY-MM-DD)
//    - assignmentsMap
// ------------------------------------------------------------
export function getCarForDriverByDate({ driverId, date, assignmentsMap }) {
  console.log(`🔍 getCarForDriverByDate: Looking for driver ${driverId} on date ${date}`);
  
  const assignments = assignmentsMap[driverId] || [];
  if (!assignments.length) {
    console.warn(`❌ getCarForDriverByDate: No assignments found for driver ${driverId}`);
    return null;
  }

  const d = new Date(date);
  if (isNaN(d.getTime())) {
    console.warn(`❌ getCarForDriverByDate: Invalid date format ${date}`);
    return null;
  }
  
  d.setHours(0, 0, 0, 0);

  console.log(`📅 Target date (normalized): ${d.toISOString()}`);
  console.log(`📊 Driver has ${assignments.length} assignments to check`);

  for (const a of assignments) {
    const start = new Date(a.startDate);
    const end = a.endDate ? new Date(a.endDate) : new Date(9999, 0, 1);

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const isWithinRange = d >= start && d <= end;
    console.log(`   Assignment: ${a.vehicleNumber} from ${start.toISOString().split('T')[0]} to ${end.toISOString().split('T')[0]} - Within range? ${isWithinRange}`);

    if (isWithinRange) {
      console.log(`✅ FOUND: Driver ${driverId} assigned to ${a.vehicleNumber} on ${date}`);
      return a;
    }
  }

  console.warn(`❌ NOT FOUND: No assignment for driver ${driverId} on ${date}`);
  console.log(`   Available assignments for this driver:`);
  assignments.forEach((a, i) => {
    const start = new Date(a.startDate);
    const end = a.endDate ? new Date(a.endDate) : new Date(9999, 0, 1);
    console.log(`   ${i+1}. ${a.vehicleNumber}: ${start.toISOString().split('T')[0]} - ${end.toISOString().split('T')[0]}`);
  });

  return null;
}